'use client';

import Image from 'next/image';
import Link from 'next/link';
import classes from './ResultShowcase.module.scss';
import { IconSparkles, IconArrowRight, IconTrendingUp } from '@tabler/icons-react';

type ResultShowcaseProps = {
  badge?: string;
  title: string;
  description: string;
  image: string;
  imageAlt?: string;
  badges?: string[];
  resultStats?: { label: string; value: string }[];
  ctaLabel?: string;
  ctaHref?: string;
};

export const ResultShowcase = ({ data }: { data: ResultShowcaseProps }) => {
  const { badge, title, description, image, imageAlt, badges = [], resultStats = [], ctaLabel, ctaHref } = data;

  return (
    <section className={classes.section} aria-labelledby="result-title">
      <div className={classes.container}>
        <div className={classes.card}>
          <div className={classes.content}>
            {badge && (
              <div className={classes.badgeWrapper}>
                <IconSparkles size={18} className={classes.badgeIcon} />
                <span className={classes.badge}>{badge}</span>
              </div>
            )}
            {badges.length > 0 && (
              <div className={classes.badges}>
                {badges.map((badgeItem, index) => (
                  <span key={`${badgeItem}-${index}`} className={classes.badgeItem}>
                    {badgeItem}
                  </span>
                ))}
              </div>
            )}
            <h2 id="result-title" className={classes.title}>{title}</h2>
            <p className={classes.description}>{description}</p>
            {resultStats.length > 0 && (
              <div className={classes.stats} role="list">
                {resultStats.map((stat, index) => (
                  <div key={`${stat.label}-${index}`} className={classes.statCard} role="listitem">
                    <div className={classes.statValueWrapper}>
                      <span className={classes.statValue}>{stat.value}</span>
                      {(stat.value.includes('+') || stat.value.includes('%')) && (
                        <IconTrendingUp size={20} className={classes.trendIcon} />
                      )}
                    </div>
                    <span className={classes.statLabel}>{stat.label}</span>
                  </div>
                ))}
              </div>
            )}
            {ctaLabel && ctaHref && (
              <Link href={ctaHref} className={classes.cta}>
                {ctaLabel}
                <IconArrowRight size={18} className={classes.ctaIcon} />
              </Link>
            )}
          </div>
          <div className={classes.visual}>
            <div className={classes.imageWrapper}>
              <Image
                src={image}
                alt={imageAlt ?? title}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className={classes.image}
              />
              <div className={classes.imageOverlay} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

